import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { coursesAPI, learningAPI } from '../../services/api';
import CustomVideoPlayer from '../../components/CustomVideoPlayer';

const CourseLearn = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeLesson, setActiveLesson] = useState(null);
    const [expandedModule, setExpandedModule] = useState(0);
    const [isCinemaMode, setIsCinemaMode] = useState(false);
    const [activeFeedbackTab, setActiveFeedbackTab] = useState('overview'); // 'overview', 'speech', 'insights'
    const [zenCoinsEarned, setZenCoinsEarned] = useState(0);
    const [achievements, setAchievements] = useState([]);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [watchedTimeSeconds, setWatchedTimeSeconds] = useState(0);
    const [coinsEarnedThisLesson, setCoinsEarnedThisLesson] = useState(0);
    const [coinIncrementAnimation, setCoinIncrementAnimation] = useState(null);
    const [dayStreak, setDayStreak] = useState(0);
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [totalLessonsWatched, setTotalLessonsWatched] = useState(0);
    const [lessonsCompleted, setLessonsCompleted] = useState(new Set());
    const [totalSessionTime, setTotalSessionTime] = useState(0); // Total watch time for all lessons in this session

    // Load ZenCoins and session time from localStorage on mount
    useEffect(() => {
        const savedCoins = localStorage.getItem('totalZenCoins');
        if (savedCoins) {
            setZenCoinsEarned(parseInt(savedCoins) || 0);
        }
        
        // Load session watch time
        const sessionKey = `sessionWatchTime_${id}`;
        const savedSessionTime = localStorage.getItem(sessionKey);
        if (savedSessionTime) {
            setTotalSessionTime(parseInt(savedSessionTime) || 0);
        }
    }, [id]);

    useEffect(() => {
        const fetchCourse = async () => {
            try {
                const response = await coursesAPI.getById(id);
                const data = response.data;
                const fullJson = data.full_json || {};
                const normalized = {
                    ...data,
                    duration: data.duration || fullJson.duration,
                    prerequisites: data.prerequisites || fullJson.prerequisites,
                    learningObjectives: data.learningObjectives || fullJson.learningObjectives,
                    tags: data.tags || fullJson.tags,
                    language: data.language || fullJson.language,
                    modules: data.modules || fullJson.modules || []
                };

                setCourse(normalized);

                const firstLesson = normalized.modules
                    .flatMap((module) => module.lessons || [])
                    .find((lesson) => lesson) || null;

                setActiveLesson(firstLesson);
                
                // Auto-enroll user in the course (idempotent)
                learningAPI.enrollInCourse(parseInt(id))
                    .then(() => console.log('Enrolled in course'))
                    .catch(err => console.error('Enrollment error:', err));
            } catch (error) {
                console.error('Failed to load course', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourse();
        
        // Initialize day streak from localStorage
        const initializeStreak = () => {
            const streakData = localStorage.getItem(`courseStreakData_${id}`);
            const today = new Date().toDateString();
            
            if (!streakData) {
                localStorage.setItem(`courseStreakData_${id}`, JSON.stringify({
                    lastDate: today,
                    streak: 1,
                    totalLessons: 0
                }));
                setDayStreak(1);
            } else {
                const data = JSON.parse(streakData);
                const lastDate = data.lastDate;
                
                if (lastDate !== today) {
                    // Check if it's consecutive day
                    const lastDateObj = new Date(lastDate);
                    const todayObj = new Date(today);
                    const timeDiff = (todayObj - lastDateObj) / (1000 * 60 * 60 * 24);
                    
                    if (timeDiff === 1) {
                        // Consecutive day - increment streak
                        data.streak += 1;
                    } else if (timeDiff > 1) {
                        // Broke the streak
                        data.streak = 1;
                    }
                    data.lastDate = today;
                    localStorage.setItem(`courseStreakData_${id}`, JSON.stringify(data));
                }
                setDayStreak(data.streak || 1);
                setTotalLessonsWatched(data.totalLessons || 0);
            }
        };
        
        initializeStreak();
    }, [id]);

    const allLessonsCount = useMemo(() => {
        if (!course?.modules) return 0;
        return course.modules.reduce((count, module) => count + (module.lessons?.length || 0), 0);
    }, [course]);

    // Track watch time and auto-increment coins while video is playing
    useEffect(() => {
        if (!isVideoPlaying) return;

        const watchInterval = setInterval(() => {
            setWatchedTimeSeconds((prev) => {
                const newTime = prev + 1;
                
                // Calculate lesson duration in seconds (assuming format like "10 min")
                const durationStr = activeLesson?.duration || "10 min";
                const durationMinutes = parseInt(durationStr) || 10;
                const durationSeconds = durationMinutes * 60;
                
                // Calculate completion percentage
                const completion = Math.min(100, Math.round((newTime / durationSeconds) * 100));
                setCompletionPercentage(completion);
                
                // Mark lesson as completed at 80% watch time
                if (completion >= 80 && activeLesson && !lessonsCompleted.has(activeLesson.id)) {
                    setLessonsCompleted((prev) => new Set([...prev, activeLesson.id]));
                    
                    // Call backend to record completion and award XP/coins
                    learningAPI.completeLesson(
                        parseInt(id), 
                        activeLesson.id, 
                        activeLesson.title
                    ).then(response => {
                        if (response.data.success) {
                            console.log('Lesson completed:', response.data);
                            // Show notification with XP/coins earned
                            const rewards = response.data;
                            if (rewards.xp_earned) {
                                // Could add a toast notification here
                                console.log(`Earned ${rewards.xp_earned} XP and ${rewards.coins_earned} ZenCoins!`);
                            }
                        }
                    }).catch(err => {
                        console.error('Failed to record lesson completion:', err);
                    });
                    
                    // Update total lessons watched and streak data
                    const streakData = localStorage.getItem(`courseStreakData_${id}`);
                    if (streakData) {
                        const data = JSON.parse(streakData);
                        data.totalLessons = (data.totalLessons || 0) + 1;
                        localStorage.setItem(`courseStreakData_${id}`, JSON.stringify(data));
                        setTotalLessonsWatched(data.totalLessons);
                    }
                }
                
                // Award coins every 30 seconds of watch time, max 50 coins per lesson
                if (newTime % 30 === 0 && coinsEarnedThisLesson < 50) {
                    const coinsToAdd = 5;
                    setZenCoinsEarned((current) => current + coinsToAdd);
                    setCoinsEarnedThisLesson((current) => current + coinsToAdd);
                    
                    // Show coin increment animation
                    setCoinIncrementAnimation(true);
                    setTimeout(() => setCoinIncrementAnimation(false), 600);
                }
                
                // Track total session watch time
                setTotalSessionTime((prevTotal) => {
                    const newTotal = prevTotal + 1;
                    localStorage.setItem(`sessionWatchTime_${id}`, newTotal.toString());
                    return newTotal;
                });
                
                return newTime;
            });
        }, 1000);

        return () => clearInterval(watchInterval);
    }, [isVideoPlaying, coinsEarnedThisLesson, activeLesson, id, lessonsCompleted]);

    // Sync ZenCoins to localStorage for global access (navbar, profile, etc.)
    useEffect(() => {
        if (zenCoinsEarned > 0) {
            localStorage.setItem('totalZenCoins', zenCoinsEarned.toString());
            // Dispatch custom event to notify other components
            window.dispatchEvent(new Event('zencoins-updated'));
        }
    }, [zenCoinsEarned]);

    // Reset watch stats when lesson changes
    useEffect(() => {
        if (activeLesson) {
            setWatchedTimeSeconds(0);
            setCoinsEarnedThisLesson(0);
            setCoinIncrementAnimation(false);
        }
    }, [activeLesson?.id]);

    const handleLessonSelect = (lesson) => {
        setActiveLesson(lesson);
        if (isCinemaMode) {
            setIsCinemaMode(false);
        }
        // Simulate AI analysis and ZenCoins reward
        generateAIFeedback(lesson);
    };

    // Format watched time like Duolingo (e.g., "2m 30s", "5 min", "1h 15m")
    const formatWatchedTime = (seconds) => {
        if (seconds < 60) {
            return `${seconds}s`;
        } else if (seconds < 3600) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            if (secs === 0) {
                return `${mins} min`;
            }
            return `${mins}m ${secs}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            if (mins === 0) {
                return `${hours}h`;
            }
            return `${hours}h ${mins}m`;
        }
    };

    const generateAIFeedback = (lesson) => {
        // Local achievements display (real tracking happens server-side)
        const coinsReward = Math.floor(Math.random() * 30) + 20; // 20-50 coins
        const newAchievements = [];
        
        if (coinsReward > 40) {
            newAchievements.push('⭐ Perfect Lesson');
        }
        if (lesson?.duration === '10 min' && coinsReward > 35) {
            newAchievements.push('🎯 Quick Learner');
        }
        if (dayStreak >= 3) {
            newAchievements.push('🔥 Consistency Pro');
        }
        if (totalLessonsWatched >= 5) {
            newAchievements.push('📚 Eager Student');
        }
        if (zenCoinsEarned >= 100) {
            newAchievements.push('💰 Coin Collector');
        }
        
        setAchievements(newAchievements);
        setActiveFeedbackTab('overview');
    };

    const handleVideoEnd = () => {
        // Redirect to activity page when video ends
        if (activeLesson) {
            navigate(`/courses/${id}/lesson/${activeLesson.id}/activity`, {
                state: {
                    title: activeLesson.title,
                    description: activeLesson.description,
                    videoUrl: activeLesson.videoUrl,
                    courseTitle: course?.title
                }
            });
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Loading your course...</p>
            </div>
        );
    }

    if (!course) {
        return (
            <div style={styles.notFoundContainer}>
                <h2 style={styles.notFoundTitle}>Course not found</h2>
                <p style={styles.notFoundText}>We could not load this course right now.</p>
                <button onClick={() => navigate('/courses')} style={styles.backButton}>
                    Back to Courses
                </button>
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            <style>
                {`
                    @keyframes float-up {
                        0% {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                        100% {
                            opacity: 0;
                            transform: translateY(-60px) scale(0.8);
                        }
                    }
                `}
            </style>
            <div style={styles.header}>
                <button onClick={() => navigate(`/courses/${course.id}`)} style={styles.breadcrumb}>
                    ← Back to Course Details
                </button>
                <div style={styles.headerTitle}>{course.title}</div>
                <div style={styles.headerMeta}>
                    <span style={styles.metaPill}>{course.level || 'All Levels'}</span>
                    <span style={styles.metaPill}>{course.language || 'English'}</span>
                    <span style={styles.metaPill}>{allLessonsCount} lessons</span>
                </div>
            </div>

            <div style={styles.contentGrid}>
                <div style={styles.playerColumn}>
                    <CustomVideoPlayer
                        src={activeLesson?.videoUrl}
                        poster={course.image}
                        title={activeLesson?.title}
                        subtitle={activeLesson?.description}
                        isCinemaMode={isCinemaMode}
                        onToggleCinema={() => setIsCinemaMode(!isCinemaMode)}
                        onPlayStateChange={setIsVideoPlaying}
                        onVideoEnd={handleVideoEnd}
                    />

                    <div style={styles.lessonMeta}>
                        <div style={styles.lessonTitle}>
                            {activeLesson?.title || 'Select a lesson to begin'}
                        </div>
                        <div style={styles.lessonDescription}>
                            {activeLesson?.description || 'Choose a lesson from the curriculum to start learning.'}
                        </div>
                    </div>

                    {/* Coin Increment Animation */}
                    {coinIncrementAnimation && (
                        <div style={{
                            position: 'fixed',
                            bottom: '120px',
                            right: '30px',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#27b05b',
                            animation: 'float-up 0.6s ease-out forwards',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            textShadow: '0 2px 8px rgba(39, 176, 91, 0.3)'
                        }}>
                            +5 💰
                        </div>
                    )}

                    {/* AI Feedback Tabs */}
                    <div style={styles.feedbackSection}>
                        <div style={styles.feedbackTabs}>
                            <button
                                style={activeFeedbackTab === 'overview' ? styles.tabBtnActive : styles.tabBtn}
                                onClick={() => setActiveFeedbackTab('overview')}
                            >
                                📊 Overview
                            </button>
                            <button
                                style={activeFeedbackTab === 'speech' ? styles.tabBtnActive : styles.tabBtn}
                                onClick={() => setActiveFeedbackTab('speech')}
                            >
                                🎤 Speech Analysis
                            </button>
                            <button
                                style={activeFeedbackTab === 'insights' ? styles.tabBtnActive : styles.tabBtn}
                                onClick={() => setActiveFeedbackTab('insights')}
                            >
                                💡 Lesson Insights
                            </button>
                        </div>

                        {/* Overview Tab */}
                        {activeFeedbackTab === 'overview' && (
                            <div style={styles.tabContent}>
                                <div style={styles.overviewGrid}>
                                    {/* ZenCoins Earned */}
                                    <div style={styles.metricCard}>
                                        <div style={styles.metricIcon}>💰</div>
                                        <div style={styles.metricValue}>{zenCoinsEarned}</div>
                                        <div style={styles.metricLabel}>ZenCoins Earned</div>
                                        <div style={styles.metricSubtext}>Spend in the shop</div>
                                    </div>

                                    {/* Completion */}
                                    <div style={styles.metricCard}>
                                        <div style={styles.metricIcon}>✓</div>
                                        <div style={styles.metricValue}>{completionPercentage}%</div>
                                        <div style={styles.metricLabel}>Completion</div>
                                        <div style={styles.metricSubtext}>Lesson watched</div>
                                    </div>

                                    {/* Time Spent */}
                                    <div style={styles.metricCard}>
                                        <div style={styles.metricIcon}>⏱️</div>
                                        <div style={styles.metricValue}>{formatWatchedTime(totalSessionTime)}</div>
                                        <div style={styles.metricLabel}>Time Spent</div>
                                        <div style={styles.metricSubtext}>
                                            {totalSessionTime >= 300 ? 'Amazing dedication!' : 
                                             totalSessionTime >= 120 ? 'Keep going!' : 
                                             totalSessionTime >= 60 ? 'Great start!' : 
                                             'Start watching to track'}
                                        </div>
                                    </div>

                                    {/* Streak */}
                                    <div style={styles.metricCard}>
                                        <div style={styles.metricIcon}>🔥</div>
                                        <div style={styles.metricValue}>{dayStreak}</div>
                                        <div style={styles.metricLabel}>Day Streak</div>
                                        <div style={styles.metricSubtext}>Keep it going!</div>
                                    </div>
                                </div>

                                {/* Achievements */}
                                {achievements.length > 0 && (
                                    <div style={styles.achievementsBox}>
                                        <div style={styles.achievementsTitle}>🏆 New Achievements!</div>
                                        <div style={styles.achievementsList}>
                                            {achievements.map((achievement, idx) => (
                                                <div key={idx} style={styles.achievementBadge}>
                                                    {achievement}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Speech Analysis Tab */}
                        {activeFeedbackTab === 'speech' && (
                            <div style={styles.tabContent}>
                                <div style={styles.feedbackBox}>
                                    <div style={styles.feedbackTitle}>🎤 Speech Analysis</div>
                                    <div style={styles.feedbackGrid}>
                                        <div style={styles.scoreCard}>
                                            <div style={styles.scoreLabel}>Fluency Score</div>
                                            <div style={styles.scoreValue}>{Math.min(100, 60 + Math.floor(completionPercentage * 0.4))}/100</div>
                                            <div style={styles.scoreBar}>
                                                <div style={{...styles.scoreBarFill, width: `${Math.min(100, 60 + Math.floor(completionPercentage * 0.4))}%`}}></div>
                                            </div>
                                            <div style={styles.scoreHint}>Good pace, work on natural pauses</div>
                                        </div>

                                        <div style={styles.scoreCard}>
                                            <div style={styles.scoreLabel}>Grammar Score</div>
                                            <div style={styles.scoreValue}>{Math.min(100, 70 + Math.floor(completionPercentage * 0.3))}/100</div>
                                            <div style={styles.scoreBar}>
                                                <div style={{...styles.scoreBarFill, width: `${Math.min(100, 70 + Math.floor(completionPercentage * 0.3))}%`}}></div>
                                            </div>
                                            <div style={styles.scoreHint}>Excellent sentence structure</div>
                                        </div>

                                        <div style={styles.scoreCard}>
                                            <div style={styles.scoreLabel}>Pronunciation Score</div>
                                            <div style={styles.scoreValue}>{Math.min(100, 55 + Math.floor(completionPercentage * 0.45))}/100</div>
                                            <div style={styles.scoreBar}>
                                                <div style={{...styles.scoreBarFill, width: `${Math.min(100, 55 + Math.floor(completionPercentage * 0.45))}%`}}></div>
                                            </div>
                                            <div style={styles.scoreHint}>Focus on vowel clarity</div>
                                        </div>

                                        <div style={styles.scoreCard}>
                                            <div style={styles.scoreLabel}>Confidence Index</div>
                                            <div style={styles.scoreValue}>{Math.min(100, 65 + Math.floor(completionPercentage * 0.35))}/100</div>
                                            <div style={styles.scoreBar}>
                                                <div style={{...styles.scoreBarFill, width: `${Math.min(100, 65 + Math.floor(completionPercentage * 0.35))}%`}}></div>
                                            </div>
                                            <div style={styles.scoreHint}>You're speaking with good confidence</div>
                                        </div>
                                    </div>

                                    <div style={styles.feedbackText}>
                                        <div style={styles.feedbackSubtitle}>Key Improvements:</div>
                                        <ul style={styles.feedbackList}>
                                            <li>Reduce filler words (um, uh, like) - use strategic pauses instead</li>
                                            <li>Practice the 'th' sound in "the" and "think"</li>
                                            <li>Work on stress patterns in multi-syllable words</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lesson Insights Tab */}
                        {activeFeedbackTab === 'insights' && (
                            <div style={styles.tabContent}>
                                <div style={styles.feedbackBox}>
                                    <div style={styles.feedbackTitle}>💡 Lesson Insights</div>

                                    <div style={styles.insightSection}>
                                        <div style={styles.insightSubtitle}>📝 Key Takeaways</div>
                                        <ul style={styles.feedbackList}>
                                            <li>Introduction to confident speaking techniques</li>
                                            <li>The importance of body language in communication</li>
                                            <li>How to manage nervousness before speaking</li>
                                            <li>Practice exercises for daily improvement</li>
                                        </ul>
                                    </div>

                                    <div style={styles.insightSection}>
                                        <div style={styles.insightSubtitle}>🎯 Practice Questions</div>
                                        <div style={styles.questionCard}>
                                            <div style={styles.questionText}>What are 3 techniques to manage speaking anxiety?</div>
                                            <button style={styles.practiceBtn}>Answer Now</button>
                                        </div>
                                        <div style={styles.questionCard}>
                                            <div style={styles.questionText}>How can body language improve your presentation?</div>
                                            <button style={styles.practiceBtn}>Answer Now</button>
                                        </div>
                                    </div>

                                    <div style={styles.insightSection}>
                                        <div style={styles.insightSubtitle}>📚 Related Resources</div>
                                        <div style={styles.resourceLink}>
                                            <span>→ Download: Speaking Confidence Checklist</span>
                                        </div>
                                        <div style={styles.resourceLink}>
                                            <span>→ Read: Tips for Public Speaking Anxiety</span>
                                        </div>
                                        <div style={styles.resourceLink}>
                                            <span>→ Watch: Next Lesson - Handling Questions</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.sidebar}>
                    <div style={styles.sidebarHeader}>
                        <div style={styles.sidebarTitle}>Course Curriculum</div>
                        <div style={styles.sidebarSubtitle}>Pick a lesson to start watching</div>
                    </div>

                    <div style={styles.moduleList}>
                        {course.modules?.map((module, moduleIndex) => (
                            <div key={moduleIndex} style={styles.moduleCard}>
                                <button
                                    style={styles.moduleHeader}
                                    onClick={() => setExpandedModule(expandedModule === moduleIndex ? -1 : moduleIndex)}
                                >
                                    <div>
                                        <div style={styles.moduleLabel}>Module {moduleIndex + 1}</div>
                                        <div style={styles.moduleTitle}>{module.title}</div>
                                    </div>
                                    <span style={styles.expandIcon}>
                                        {expandedModule === moduleIndex ? '−' : '+'}
                                    </span>
                                </button>

                                {expandedModule === moduleIndex && (
                                    <div style={styles.lessonList}>
                                        {(module.lessons || []).map((lesson, lessonIndex) => (
                                            <button
                                                key={lessonIndex}
                                                style={
                                                    activeLesson?.title === lesson.title
                                                        ? { ...styles.lessonButton, ...styles.lessonButtonActive }
                                                        : styles.lessonButton
                                                }
                                                onClick={() => handleLessonSelect(lesson)}
                                            >
                                                <span style={styles.lessonIcon}>
                                                    {lesson.videoUrl ? '▶' : '📄'}
                                                </span>
                                                <div style={styles.lessonTextBlock}>
                                                    <div style={styles.lessonName}>{lesson.title || `Lesson ${lessonIndex + 1}`}</div>
                                                    {lesson.duration && (
                                                        <div style={styles.lessonDuration}>{lesson.duration}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: '#eef2f8',
        padding: '90px 20px 60px'
    },
    header: {
        maxWidth: '1200px',
        margin: '0 auto 30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    breadcrumb: {
        background: 'transparent',
        border: 'none',
        color: '#27b05b',
        fontWeight: '600',
        cursor: 'pointer',
        padding: 0,
        width: 'fit-content'
    },
    headerTitle: {
        fontSize: 'clamp(22px, 4vw, 30px)',
        fontWeight: '800',
        color: '#111827'
    },
    headerMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px'
    },
    metaPill: {
        backgroundColor: '#e8f7ef',
        color: '#127a3f',
        padding: '6px 12px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: '700'
    },
    contentGrid: {
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px'
    },
    playerColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    lessonMeta: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        padding: '20px',
        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
    },
    lessonTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: '8px'
    },
    lessonDescription: {
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.7'
    },
    sidebar: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
        height: 'fit-content'
    },
    sidebarHeader: {
        marginBottom: '18px'
    },
    sidebarTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#111827'
    },
    sidebarSubtitle: {
        fontSize: '13px',
        color: '#6b7280',
        marginTop: '4px'
    },
    moduleList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
    },
    moduleCard: {
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    moduleHeader: {
        width: '100%',
        backgroundColor: '#f9fafb',
        border: 'none',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        textAlign: 'left'
    },
    moduleLabel: {
        fontSize: '11px',
        fontWeight: '700',
        color: '#6366f1',
        textTransform: 'uppercase',
        letterSpacing: '0.08em'
    },
    moduleTitle: {
        fontSize: '15px',
        fontWeight: '700',
        color: '#111827'
    },
    expandIcon: {
        fontSize: '20px',
        color: '#6b7280'
    },
    lessonList: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 10px 12px',
        gap: '8px'
    },
    lessonButton: {
        border: '1px solid transparent',
        borderRadius: '10px',
        padding: '10px 12px',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        textAlign: 'left'
    },
    lessonButtonActive: {
        borderColor: '#6366f1',
        backgroundColor: '#eef2ff'
    },
    lessonIcon: {
        fontSize: '16px',
        color: '#4338ca'
    },
    lessonTextBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    lessonName: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#111827'
    },
    lessonDuration: {
        fontSize: '12px',
        color: '#6b7280'
    },
    loadingContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f6fb'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #4f46e5',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
    },
    loadingText: {
        fontSize: '16px',
        color: '#6b7280'
    },
    notFoundContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f6fb',
        padding: '40px 20px'
    },
    notFoundTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: '8px'
    },
    notFoundText: {
        fontSize: '15px',
        color: '#6b7280',
        marginBottom: '20px'
    },
    backButton: {
        padding: '10px 24px',
        backgroundColor: '#4f46e5',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    feedbackSection: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
    },
    feedbackTabs: {
        display: 'flex',
        gap: '8px',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: '20px'
    },
    tabBtn: {
        background: 'transparent',
        border: 'none',
        color: '#6b7280',
        fontSize: '14px',
        fontWeight: '600',
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '2px solid transparent',
        transition: 'all 0.2s'
    },
    tabBtnActive: {
        background: 'transparent',
        border: 'none',
        color: '#27b05b',
        fontSize: '14px',
        fontWeight: '600',
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '2px solid #27b05b'
    },
    tabContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    overviewGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '16px'
    },
    metricCard: {
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
        border: '1px solid #e5e7eb'
    },
    metricIcon: {
        fontSize: '28px',
        marginBottom: '8px'
    },
    metricValue: {
        fontSize: '24px',
        fontWeight: '800',
        color: '#27b05b',
        marginBottom: '4px'
    },
    metricLabel: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#111827'
    },
    metricSubtext: {
        fontSize: '12px',
        color: '#6b7280',
        marginTop: '4px'
    },
    achievementsBox: {
        backgroundColor: '#fffbeb',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid #fde68a'
    },
    achievementsTitle: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#92400e',
        marginBottom: '12px'
    },
    achievementsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    achievementBadge: {
        backgroundColor: '#fff7e6',
        border: '1px solid #fbbf24',
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#d97706'
    },
    feedbackBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    feedbackTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#111827'
    },
    feedbackGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px'
    },
    scoreCard: {
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid #e5e7eb'
    },
    scoreLabel: {
        fontSize: '12px',
        fontWeight: '700',
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: '8px'
    },
    scoreValue: {
        fontSize: '24px',
        fontWeight: '800',
        color: '#27b05b',
        marginBottom: '8px'
    },
    scoreBar: {
        width: '100%',
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '999px',
        overflow: 'hidden',
        marginBottom: '8px'
    },
    scoreBarFill: {
        height: '100%',
        backgroundColor: '#27b05b',
        transition: 'width 0.3s ease'
    },
    scoreHint: {
        fontSize: '12px',
        color: '#6b7280',
        lineHeight: '1.4'
    },
    feedbackText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    feedbackSubtitle: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#111827'
    },
    feedbackList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    insightSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    insightSubtitle: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#111827'
    },
    questionCard: {
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        border: '1px solid #e5e7eb'
    },
    questionText: {
        fontSize: '13px',
        color: '#374151',
        flex: 1
    },
    practiceBtn: {
        backgroundColor: '#27b05b',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    },
    resourceLink: {
        fontSize: '13px',
        color: '#27b05b',
        padding: '10px 12px',
        backgroundColor: '#f0f9f7',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.2s'
    }
};

export default CourseLearn;
